import { Component, ElementRef, ViewChild } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthenticationService } from '../../services/authentication.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { humanFileSize } from '../../helpers/byte-helpers';
import { generateGUID } from '../../helpers/string-helpers';
import { filter, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatList, MatListItem, MatListItemTitle, MatListItemLine, MatListItemIcon } from '@angular/material/list';
import { DropzoneComponent } from '../../components/dropzone/dropzone.component';
import { UploadApiService } from '../../../../../api/generated';
import { notNullOrUndefined } from '../../../../../shared/utils';
import { environment } from '../../../environments/environment';
import { MatCard, MatCardContent, MatCardHeader } from '@angular/material/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { FileSizePipe } from '../../pipes/file-size.pipe';
import { LanguageService } from '../../services/language.service';

enum GeneralUploadStatus {
  NotStarted,
  Uploading,
  Uploaded,
}

enum FileUploadStatus {
  NotStarted,
  Uploading,
  Validating,
  Uploaded,
}

interface SelectedFile {
  name: string;
  size: number;
  date: number;
  fileKey: string;
  numberOfChunks: number;
  progress: number;
  progressPercentage: number;
  error: string;
  createdDateTime?: number;
  file: File;
  fileUploadStatus: FileUploadStatus;
}

interface UploadChunkToGraphResponse {
  fileItem?: {
    key: string;
    chunkNumber: number;
    isCompleted: boolean;
    graphId?: string;
    graphName?: string;
    createdDateTime?: number;
    uploadSessionExpirationDateTime?: string;
    nextExpectedRanges?: string[];
  };
}

const GRAPH_CHUNK_GRANULARITY = 320 * 1024;
const DEFAULT_CHUNK_SIZE = 32 * GRAPH_CHUNK_GRANULARITY;

function getGraphChunkSize(configuredChunkSize?: number): number {
  if (!configuredChunkSize || configuredChunkSize < GRAPH_CHUNK_GRANULARITY) {
    return DEFAULT_CHUNK_SIZE;
  }

  return Math.floor(configuredChunkSize / GRAPH_CHUNK_GRANULARITY) * GRAPH_CHUNK_GRANULARITY;
}

const CHUNK_SIZE = getGraphChunkSize(environment.upload?.chunkSizeBytes);

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss',
  standalone: true,
  imports: [DropzoneComponent, MatCard, MatCardContent, MatCardHeader, MatListItemIcon, MatList, MatListItem, MatListItemTitle, MatListItemLine, MatProgressBar, DatePipe, TranslateModule, FileSizePipe]
})
export class UploadComponent {

  @ViewChild('dropzone') dropzone?: ElementRef;

  folderName: string = "";

  chosenFiles: File[] = [];
  selectedFiles: SelectedFile[] = [];
  allFiles: SelectedFile[] = [];
  generalUploadStatus = GeneralUploadStatus.NotStarted;
  creatorName: string = "";
  humanFileSize = humanFileSize;

  // Export enums to the template
  GeneralUploadStatus = GeneralUploadStatus;
  FileUploadStatus = FileUploadStatus;

  displayedColumns: string[] = ['name', 'size', 'date', 'uploaded'];

  private redirectTimeout: any;
  // getter for dropZoneEnabled
  get dropZoneEnabled() {
    return (this.generalUploadStatus === GeneralUploadStatus.NotStarted) || (this.generalUploadStatus === GeneralUploadStatus.Uploaded);
  }

  constructor(
    private http: HttpClient,
    private uploadService: UploadApiService,
    private authService: AuthenticationService,
    private _snackBar: MatSnackBar,
    private translate: TranslateService,
    private location: Location,
    private languageService: LanguageService
  ) {
    this.authService.authenticatedUser$
      .pipe(
        filter(notNullOrUndefined),
        takeUntilDestroyed()
      )
      .subscribe(user => {
        this.folderName = user.folderName;
        this.creatorName = user.creatorName;
      })
    this.redirectTimeout = setTimeout(() => {
      this.location.back();
    }, 600000);

    if (sessionStorage.getItem("language")) {
      this.languageService.setLanguage(sessionStorage.getItem("language")!);
    }
  }

  ngOnDestroy(): void {
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.generalUploadStatus = GeneralUploadStatus.NotStarted;
      this.chooseFile(input.files);
    }
  }

  chooseFile(files: FileList) {
    if (files.length === 0) {
      return;
    }

    this.selectedFiles = [];
    this.chosenFiles = Array.from(files);

    for (let i = 0; i < files.length; i++) {
      this.selectedFiles.push({
        name: files[i].name,
        size: files[i].size,
        date: files[i].lastModified,
        fileKey: generateGUID(),
        numberOfChunks: Math.ceil(files[i].size / CHUNK_SIZE),
        progress: 0,
        progressPercentage: 0,
        error: '',
        file: files[i],
        fileUploadStatus: FileUploadStatus.NotStarted
      });
    }

    this.allFiles = [...this.selectedFiles, ...this.allFiles];
    void this.uploadFiles();
  }

  async uploadFiles() {
    if (!this.selectedFiles || this.selectedFiles.length === 0) {
      return;
    }

    this.generalUploadStatus = GeneralUploadStatus.Uploading;

    const user = this.authService.authenticatedUser$.value;
    if (!user) {
      return;
    }

    for (let i = 0; i < this.selectedFiles.length; i++) {
      const selectedFile = this.selectedFiles[i];

      if (selectedFile.fileUploadStatus !== FileUploadStatus.NotStarted) {
        continue;
      }

      selectedFile.fileUploadStatus = FileUploadStatus.Uploading;

      try {
        await this.uploadSingleFileToGraph(user.id, selectedFile);
      } catch (error) {
        console.error('Upload failed', error);
        selectedFile.error = 'Upload failed';
        selectedFile.fileUploadStatus = FileUploadStatus.NotStarted;
        this.generalUploadStatus = GeneralUploadStatus.NotStarted;
        return;
      }
    }

    if (this.selectedFiles.every(x => x.fileUploadStatus === FileUploadStatus.Uploaded)) {
      const translation = await firstValueFrom(this.translate.get('upload.filesUploaded'));
      this.openSnackBar(translation);

      this.generalUploadStatus = GeneralUploadStatus.Uploaded;

      const body = {
        fileNames: this.selectedFiles.map(x => x.file.name)
      };

      console.log('sending email', body);
      await firstValueFrom(this.uploadService.postApiV10UploadSendUploadEmail(body));
      console.log('Email sent');
    }
  }

  private async uploadSingleFileToGraph(userId: string, selectedFile: SelectedFile) {
    const file = selectedFile.file;

    let start = 0;
    let chunkNumber = 0;
    let lastResponse: UploadChunkToGraphResponse | undefined;

    while (start < file.size) {
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      lastResponse = await this.uploadChunkToGraph(userId, file, selectedFile.fileKey, chunkNumber, start, end - 1, chunk);

      if (!lastResponse.fileItem) {
        throw new Error('Upload response did not contain a file item.');
      }

      selectedFile.progress++;
      selectedFile.progressPercentage = Math.round((selectedFile.progress / selectedFile.numberOfChunks) * 100);

      start = end;
      chunkNumber++;
    }

    selectedFile.fileUploadStatus = FileUploadStatus.Validating;

    if (!lastResponse?.fileItem?.isCompleted) {
      throw new Error('Graph upload session did not report completion after the final chunk.');
    }

    selectedFile.fileUploadStatus = FileUploadStatus.Uploaded;
    selectedFile.createdDateTime = lastResponse.fileItem.createdDateTime;
  }

  private async uploadChunkToGraph(
    userId: string,
    file: File,
    fileKey: string,
    chunkNumber: number,
    chunkStartByte: number,
    chunkEndByteInclusive: number,
    chunk: Blob
  ) {
    const params = new HttpParams({
      fromObject: {
        personGuid: userId,
        fileName: file.name,
        filekey: fileKey,
        chunkNumber,
        chunkStartByte,
        chunkEndByteInclusive,
        totalFileSize: file.size
      }
    });

    return await firstValueFrom(this.http.put<UploadChunkToGraphResponse>(
      `${environment.api.basePath}/api/v1.0/Upload/UploadChunkToGraph`,
      chunk,
      {
        params,
        headers: {
          'Content-Type': 'application/octet-stream'
        }
      }
    ));
  }

  openSnackBar(message: string) {
    this._snackBar.open(message, 'sluiten', { duration: 5000, panelClass: ['mat-success'] })
  }

  clearSelection(event: MouseEvent) {
    event.preventDefault();

    // unfortunately, clearing the input[type=file] element does not work, so we just reload the page
    location.reload();
  }

}
